import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api.ts';
import {
  formatCurrencyInput,
  parseCurrency,
  applyCurrencyMask,
  formatCurrency,
} from '../utils/currency.ts';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CubeIcon,
  CalendarIcon,
  DocumentTextIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

// Types
interface Asset {
  id: string;
  description: string;
  type: 'real_estate' | 'vehicle' | 'other';
  currentValue: number;
  acquisitionValue: number;
  status: string;
}

interface SaleFormData {
  saleDate: string;
  saleValue: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  paymentMethod: 'cash' | 'financing' | 'installments';
  installments?: number;
  notes: string;
}

const AssetSale: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<SaleFormData>({
    saleDate: new Date().toISOString().split('T')[0],
    saleValue: 0,
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    paymentMethod: 'cash',
    notes: '',
  });

  const [saleValueDisplay, setSaleValueDisplay] = useState<string>('R$ 0,00');

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Query para buscar dados do ativo
  const { data: asset, isLoading } = useQuery<Asset>(
    ['asset', id],
    async () => {
      const response = await api.get(`/assets/${id}`);
      return response.data;
    },
    {
      onSuccess: data => {
        // Pré-preencher o valor de venda com o valor atual
        if (formData.saleValue === 0) {
          setFormData(prev => ({ ...prev, saleValue: data.currentValue }));
        }
      },
    }
  );

  // Mutation para registrar venda
  const saleMutation = useMutation(
    async (data: SaleFormData) => {
      const response = await api.post(`/assets/${id}/sell`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['asset', id]);
        queryClient.invalidateQueries(['assets']);
        navigate(`/assets/${id}`);
      },
      onError: (error: any) => {
        console.error('Erro ao registrar venda:', error);
      },
    }
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.saleDate) {
      newErrors.saleDate = 'Data da venda é obrigatória';
    }

    if (formData.saleValue <= 0) {
      newErrors.saleValue = 'Valor da venda deve ser maior que zero';
    }

    if (!formData.buyerName.trim()) {
      newErrors.buyerName = 'Nome do comprador é obrigatório';
    }

    if (formData.buyerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.buyerEmail)) {
      newErrors.buyerEmail = 'Email inválido';
    }

    if (
      formData.paymentMethod === 'installments' &&
      (!formData.installments || formData.installments < 2)
    ) {
      newErrors.installments = 'Número de parcelas deve ser maior que 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      saleMutation.mutate(formData);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'real_estate':
        return <BuildingOfficeIcon className="h-5 w-5" />;
      case 'vehicle':
        return <TruckIcon className="h-5 w-5" />;
      default:
        return <CubeIcon className="h-5 w-5" />;
    }
  };

  const getPaymentMethodText = (method: string) => {
    switch (method) {
      case 'cash':
        return 'À vista';
      case 'financing':
        return 'Financiamento';
      case 'installments':
        return 'Parcelado';
      default:
        return method;
    }
  };

  const calculateProfit = () => {
    if (!asset) return 0;
    return formData.saleValue - asset.acquisitionValue;
  };

  const calculateProfitPercentage = () => {
    if (!asset || asset.acquisitionValue === 0) return 0;
    return ((formData.saleValue - asset.acquisitionValue) / asset.acquisitionValue) * 100;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/assets/${id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Detalhes do Ativo
          </button>

          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <CurrencyDollarIcon className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Registrar Venda</h1>
              <p className="text-gray-600 mt-1">Vendendo: {asset?.description}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Ativo a ser Vendido */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <div className="p-2 rounded-full bg-red-100 text-red-600 mr-3">
                {asset && getTypeIcon(asset.type)}
              </div>
              Ativo a ser Vendido
            </h3>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-gray-900">{asset?.description}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Valor atual: {formatCurrency(asset?.currentValue || 0)}
                  </p>
                </div>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                  Será Vendido
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Valor de Aquisição:</span>
                  <span className="ml-2 font-medium">
                    {formatCurrency(asset?.acquisitionValue || 0)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Valor Atual:</span>
                  <span className="ml-2 font-medium">
                    {formatCurrency(asset?.currentValue || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Informações da Venda */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Informações da Venda</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data da Venda *
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="date"
                    value={formData.saleDate}
                    onChange={e => setFormData(prev => ({ ...prev, saleDate: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.saleDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.saleDate && <p className="mt-1 text-sm text-red-600">{errors.saleDate}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor da Venda *
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={saleValueDisplay}
                    onChange={e => {
                      const formattedValue = applyCurrencyMask(e.target.value);
                      setSaleValueDisplay(formattedValue);
                      const numericValue = parseCurrency(formattedValue);
                      setFormData(prev => ({ ...prev, saleValue: numericValue }));
                    }}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.saleValue ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="R$ 0,00"
                  />
                </div>
                {errors.saleValue && (
                  <p className="mt-1 text-sm text-red-600">{errors.saleValue}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Forma de Pagamento *
                </label>
                <select
                  value={formData.paymentMethod}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, paymentMethod: e.target.value as any }))
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="cash">À vista</option>
                  <option value="financing">Financiamento</option>
                  <option value="installments">Parcelado</option>
                </select>
              </div>

              {formData.paymentMethod === 'installments' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Parcelas *
                  </label>
                  <input
                    type="number"
                    min="2"
                    value={formData.installments || ''}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        installments: parseInt(e.target.value) || undefined,
                      }))
                    }
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.installments ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="12"
                  />
                  {errors.installments && (
                    <p className="mt-1 text-sm text-red-600">{errors.installments}</p>
                  )}
                </div>
              )}
            </div>

            {/* Resumo Financeiro */}
            {formData.saleValue > 0 && asset && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Resumo Financeiro</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Valor de Aquisição:</span>
                    <div className="font-medium">{formatCurrency(asset.acquisitionValue)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Valor de Venda:</span>
                    <div className="font-medium">{formatCurrency(formData.saleValue)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Resultado:</span>
                    <div
                      className={`font-medium ${calculateProfit() >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {calculateProfit() >= 0 ? '+' : ''}
                      {formatCurrency(Math.abs(calculateProfit()))}
                      <span className="text-xs ml-1">
                        ({calculateProfitPercentage() >= 0 ? '+' : ''}
                        {calculateProfitPercentage().toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Informações do Comprador */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <UserIcon className="h-6 w-6 mr-2 text-primary-600" />
              Informações do Comprador
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.buyerName}
                    onChange={e => setFormData(prev => ({ ...prev, buyerName: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.buyerName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Nome do comprador"
                  />
                </div>
                {errors.buyerName && (
                  <p className="mt-1 text-sm text-red-600">{errors.buyerName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.buyerEmail}
                    onChange={e => setFormData(prev => ({ ...prev, buyerEmail: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.buyerEmail ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="email@exemplo.com"
                  />
                </div>
                {errors.buyerEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.buyerEmail}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.buyerPhone}
                    onChange={e => setFormData(prev => ({ ...prev, buyerPhone: e.target.value }))}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Observações */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Observações</h3>

            <div className="relative">
              <DocumentTextIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <textarea
                value={formData.notes}
                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={4}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Detalhes adicionais sobre a venda, condições especiais, etc..."
              />
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/assets/${id}`)}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saleMutation.isLoading}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center"
            >
              {saleMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processando...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Registrar Venda
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetSale;
